import {
  Card,
  CardBody,
  COLOR,
  getColorClassName,
  Inline,
  LineBreak,
  Link,
  Paragraph,
  Separator,
} from "@george-labs.com/design-system"
import { LoanCaseDTO } from "@smartx/mikro-vt-client-api"
import dayjs from "../../utils/dayjs"
import { FC, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"

import { useAppDispatch } from "../../app/hooks"
import {
  isBiancoCase,
  isPalCase,
  isPbCase,
  isSzkCase,
} from "../../functions/helpers"
import { useProductFieldLabelUtil } from "../../hooks/useGetProductFieldLabel"
import Ellipsis from "../../functions/Ellipsis"
import { useOfferScreenFlow } from "../../hooks/useOfferScreenFlow"
import { setCase } from "../../app/slices/casesSlice"
import StatusBadge from "../../components/StatusBadge/StatusBadge"

interface CaseItemProps {
  item: LoanCaseDTO
}

const STATUSES_FOR_BOOKING_CODE = [
  "PAL_CONTRACT_SIGNATURE_JBPM_PROCESS_STARTING",
  "PAL_CONTRACT_SIGNATURE_JBPM_PROCESS_STARTED",
  "PAL_CONTRACT_SIGNATURE",
]

export const CaseItem: FC<CaseItemProps> = ({ item }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const utils = useProductFieldLabelUtil()
  const { goToNextOfferScreen } = useOfferScreenFlow()
  const showBookingCode = STATUSES_FOR_BOOKING_CODE?.includes(
    item?.loanCaseStatus?.status,
  )

  const getDate = useCallback(
    (date: string) =>
      dayjs(date).isValid()
        ? dayjs(date).format("YYYY.MM.DD")
        : t("common.noDate"),
    [t],
  )

  const handleItemClick = () => {
    dispatch(setCase(item))

    if (item.offerState) {
      goToNextOfferScreen(item.caseId, null, true)
      return
    }

    navigate(`/overview/${item.caseId}`)
  }

  return (
    <Card nested={false}>
      <CardBody
        style={{
          backgroundImage: item.productPictureUrl,
          backgroundPosition: "center",
          height: "150px",
        }}
      />
      <CardBody>
        <Paragraph className="pb-2">
          <StatusBadge
            loanCaseStatus={item.loanCaseStatus}
            caseId={item.caseId}
          />
        </Paragraph>
        <Paragraph style={{ height: "4.5em" }} className="mb-2">
          <Inline emphasis>
            <Ellipsis text={utils.productNameUtil(item)} />
          </Inline>
        </Paragraph>
        <Inline
          className={getColorClassName(COLOR.GRAY_400, { foreground: true })}
        >
          Igénylés
        </Inline>
      </CardBody>

      <Separator />

      <CardBody>
        <Paragraph small>
          <Inline emphasis>{t("case.fields.cjkNumber")}</Inline>
          <LineBreak />
          <Inline>{item.cjkNumber}</Inline>
        </Paragraph>
        {(isPbCase(item) || isPalCase(item) || isBiancoCase(item)) && (
          <Paragraph small>
            <Inline emphasis>{t("case.fields.receptionDate")}</Inline>
            <LineBreak />
            <Inline>
              {getDate(item.registrationDateManual || item.receptionDate)}
            </Inline>
          </Paragraph>
        )}
        {isSzkCase(item) && (
          <Paragraph small>
            <Inline emphasis>{t("case.fields.registrationDateManual")}</Inline>
            <LineBreak />
            <Inline>{getDate(item.registrationDateManual)}</Inline>
          </Paragraph>
        )}
        {showBookingCode && !!item.bookingCode && (
          <Paragraph small>
            <Inline emphasis>{t("case.fields.bookingCode")}</Inline>
            <LineBreak />
            <Inline>{item.bookingCode}</Inline>
          </Paragraph>
        )}
      </CardBody>

      <Separator />

      <CardBody>
        <Link onClick={handleItemClick}>{t("common.button.next")}</Link>
      </CardBody>
    </Card>
  )
}
